import React from 'react';
import { InstagramIcon } from '@revfluence/fresh-icons/brands/esm';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { cn } from '@/shadcn/lib/utils';

export interface CardUsernameDisplayProps {
  /** Username to display */
  username: string;
  /** Platform icon to show (defaults to Instagram) */
  platformIcon?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show tooltip on hover */
  showTooltip?: boolean;
  /** Custom tooltip content */
  tooltipContent?: React.ReactNode;
  /** Max width for username container */
  maxWidth?: string;
}

/**
 * CardUsernameDisplay
 * -------------------
 * Renders a username with platform icon in the top-left corner of a card overlay.
 * Supports text truncation with tooltip, customizable platform icons, and responsive sizing.
 *
 * Features:
 * - Text truncation with tooltip support
 * - Customizable platform icons (defaults to Instagram)
 * - Responsive max-width handling
 * - Consistent styling with drop shadows
 * - Accessibility support
 */
export const CardUsernameDisplay = React.forwardRef<HTMLDivElement, CardUsernameDisplayProps>(
  (
    {
      username,
      platformIcon = <InstagramIcon className="h-4 w-4 text-white drop-shadow-lg flex-shrink-0" />,
      className,
      showTooltip = true,
      tooltipContent,
      maxWidth = 'max-w-[calc(100%-4.5rem)]',
      ...props
    },
    ref,
  ) => {
    const displayContent = (
      <div className="flex items-center gap-2">
        {platformIcon}
        <span className="text-white text-sm font-medium drop-shadow-lg truncate">@{username}</span>
      </div>
    );

    const containerContent = (
      <div ref={ref} className={cn('absolute top-3 left-3', maxWidth, className)} {...props}>
        {displayContent}
      </div>
    );

    if (showTooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{containerContent}</TooltipTrigger>
            <TooltipContent>{tooltipContent || <p>@{username}</p>}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return containerContent;
  },
);

CardUsernameDisplay.displayName = 'CardUsernameDisplay';
